import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const COPY = {
  subscription: { title: "You're upgraded! 🎉", body: (p) => `Your truck is now on the ${p} plan. Enjoy your new perks.`, cta: "Go to dashboard", to: "/dashboard/owner" },
  event: { title: "Event published! 🎉", body: () => "Your hosting fee is paid and your event is now live for food trucks to apply.", cta: "View my events", to: "/dashboard/organizer" },
  corporate: { title: "Booking request sent! 🎉", body: () => "Your booking fee is paid. Local trucks now have your request and will reach out with quotes.", cta: "Back home", to: "/" },
  commission: { title: "Commission paid ✓", body: () => "Thanks! Your 10% commission has been settled.", cta: "Go to dashboard", to: "/dashboard/owner" },
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking");
  const [plan, setPlan] = useState(null);
  const attempts = useRef(0);
  const type = params.get("type") || "subscription";
  const oneTime = ["event", "corporate", "commission"].includes(type);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }
    let cancelled = false;
    const endpoint = oneTime ? `/payments/onetime/status/${sessionId}` : `/payments/status/${sessionId}`;

    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(endpoint);
        if (data.payment_status === "paid") {
          setPlan(data.plan);
          setState("paid");
          if (!oneTime) refreshUser?.();
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch { /* keep polling */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [params, refreshUser, endpointDep(oneTime)]);

  const copy = COPY[type] || COPY.subscription;

  return (
    <div className="max-w-md mx-auto px-5 py-24 text-center">
      {state === "checking" && (
        <>
          <Loader2 className="w-14 h-14 text-[#0FB5A6] mx-auto animate-spin" />
          <h1 className="font-display text-3xl font-black mt-5">Confirming your payment…</h1>
          <p className="text-slate-500 mt-2">Hang tight, this only takes a moment.</p>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="w-16 h-16 text-[#0FB5A6] mx-auto" />
          <h1 className="font-display text-3xl font-black mt-5">{copy.title}</h1>
          <p className="text-slate-500 mt-2 capitalize">{copy.body(plan)}</p>
          <Button onClick={() => navigate(copy.to)} className="mt-6 rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white" data-testid="go-dashboard-btn">{copy.cta}</Button>
        </>
      )}
      {(state === "failed" || state === "timeout") && (
        <>
          <XCircle className="w-16 h-16 text-slate-400 mx-auto" />
          <h1 className="font-display text-3xl font-black mt-5">{state === "timeout" ? "Still processing" : "Payment not completed"}</h1>
          <p className="text-slate-500 mt-2">{state === "timeout" ? "Your payment is taking longer than usual. Check your dashboard shortly." : "No worries — you can try again anytime."}</p>
          <Link to={copy.to}><Button className="mt-6 rounded-full font-bold bg-[#0FB5A6] hover:bg-[#0E9E92] text-white">Continue</Button></Link>
        </>
      )}
    </div>
  );
}

function endpointDep(oneTime) { return oneTime ? 1 : 0; }
